import React, { useEffect, useRef, useMemo } from 'react';
import './index.css';
import DatafeedFactory from './api/DatafeedFactory';
import { widget } from '../../charting_library';
import Logger from '../../utils/Logger';

/**
 * TradingView Chart Container Component
 * 
 * IMPORTANT: This component is used in mobile WebViews (Android/iOS) and web.
 * 
 * URL Format: /[market]/[baseCoin]/[cryptoCoin]/[theme]/[priceScale]
 * 
 * Examples:
 *   - Spot:    /spot/INR/BTC/light/4
 *   - Futures: /futures/INR/BTC/dark/2
 * 
 * Legacy Support: URLs without market prefix default to 'spot'
 *   - /INR/BTC/dark/4  ->  Treated as /spot/INR/BTC/dark/4
 */
const TVChartContainer = () => {
	const tvWidgetRef = useRef(null);
	const containerRef = useRef('chart_tv_container_' + Date.now()); // Unique ID per instance

	// Parse URL parameters with market detection
	// URL format: /[market]/[baseCoin]/[cryptoCoin]/[theme]/[priceScale]
	const chartConfig = useMemo(() => {
		const { datafeed, config, market } = DatafeedFactory.createFromURL();
		
		return {
			symbol: `${config.baseCoin}/${config.cryptoCoin}`,
			cryptoCoin: config.cryptoCoin,
			baseCoin: config.baseCoin,
			theme: config.theme,
			priceScale: config.priceScale,
			timezone: config.timezone,
			market,
			datafeed
		};
	}, []); // Only parse once on mount

	// Memoize widget configuration
	const widgetOptions = useMemo(() => ({
		debug: false,
		symbol: chartConfig.symbol,
		datafeed: chartConfig.datafeed, // Use market-specific datafeed
		interval: '240', // 4H default
		timezone: chartConfig.timezone,
		container: containerRef.current,
		library_path: '/charting_library/',
		locale: 'en',
		// Disabled features - optimized for trading
		disabled_features: [
			'header_compare',
			'header_settings',
			'left_toolbar',
			'header_saveload',
			'symbol_search_hot_key',
			'header_symbol_search',
			'timeframes_toolbar',
			'use_localstorage_for_settings', // Important for multi-platform
			'display_market_status',
			'symbol_info'
		],
		
		// Enabled features - mobile-optimized
		enabled_features: [
			'move_logo_to_main_pane',
			'adaptive_logo',
			'side_toolbar_in_fullscreen_mode',
			'keep_left_toolbar_visible_on_small_screens',
			'hide_left_toolbar_by_default',
			'hide_last_na_study_output',
			'volume_force_overlay',
			'create_volume_indicator_by_default_once',
			'low_density_bars',
			'study_templates',
			'support_multicharts',
			
			// Mobile-specific
			'items_favoriting',
			'save_chart_properties_to_local_storage',
		],
		
		timeframe: '1M',
		
		// Chart storage (optional - can be disabled)
		charts_storage_url: 'https://saveload.tradingview.com',
		charts_storage_api_version: '1.1',
		client_id: 'tradingview.com',
		user_id: 'public_user_id',
		
		fullscreen: false,
		autosize: true,
		
		// Default studies - consider making this configurable
		// studies: ["RSI@tv-basicstudies", "StochasticRSI@tv-basicstudies"],
		
		studies_overrides: {
			'volume.volume ma.color': 'rgba(255, 0, 0, 0.7)',
			'volume.volume ma.linewidth': 1,
			'volume.volume ma.visible': false,
		},
		
		// Favorite intervals and chart types
		favorites: {
			intervals: ['5', '15', '30', '60', '240', '1D', '1W', '1M'],
			chartTypes: ['Area', 'Candles', 'Line']
		},
		
		// Theme-aware overrides
		overrides: {
			'mainSeriesProperties.showPriceLine': true,
			
			// Background color based on theme
			'paneProperties.background': chartConfig.theme === 'dark' ? '#131722' : '#ffffff',
			'paneProperties.vertGridProperties.color': chartConfig.theme === 'dark' ? '#363c4e' : '#e1e3e6',
			'paneProperties.horzGridProperties.color': chartConfig.theme === 'dark' ? '#363c4e' : '#e1e3e6',
			
			'symbolWatermarkProperties.transparency': 90,
			'scalesProperties.textColor': chartConfig.theme === 'dark' ? '#ffffff' : '#333333',
			
			// Candle colors
			'mainSeriesProperties.candleStyle.wickUpColor': '#26a69a',
			'mainSeriesProperties.candleStyle.upColor': '#26a69a',
			'mainSeriesProperties.candleStyle.wickDownColor': '#ef5350',
			'mainSeriesProperties.candleStyle.borderUpColor': '#26a69a',
			'mainSeriesProperties.candleStyle.borderDownColor': '#ef5350',
			'mainSeriesProperties.candleStyle.downColor': '#ef5350',
			
			// Hollow candle colors
			'mainSeriesProperties.hollowCandleStyle.wickUpColor': '#26a69a',
			'mainSeriesProperties.hollowCandleStyle.upColor': '#26a69a',
			'mainSeriesProperties.hollowCandleStyle.wickDownColor': '#ef5350',
			'mainSeriesProperties.hollowCandleStyle.borderUpColor': '#26a69a',
			'mainSeriesProperties.hollowCandleStyle.borderDownColor': '#ef5350',
			'mainSeriesProperties.hollowCandleStyle.downColor': '#ef5350',
			
			// Line style
			'mainSeriesProperties.lineStyle.color': '#2962ff',
		},
		
		theme: chartConfig.theme,
		
		// Mobile-specific settings
		custom_css_url: '/tradingview-custom.css', // Optional: add custom mobile CSS
		
		// Performance optimization
		loading_screen: { backgroundColor: chartConfig.theme === 'dark' ? '#131722' : '#ffffff' },
	}), [chartConfig]);

	// Initialize chart
	useEffect(() => {
		let isCancelled = false; // Flag to handle React 18 StrictMode double-mounting
		
		const initChart = async () => {
			if (isCancelled) return;
			
			try {
				// Create widget
				const tvWidget = new widget(widgetOptions);
				tvWidgetRef.current = tvWidget;
				
				// Expose to window for debugging (dev mode only)
				if (process.env.NODE_ENV === 'development') {
					window.tvWidget = tvWidget;
				}

				// Wait for chart to be ready
				tvWidget.onChartReady(() => {
					if (isCancelled) return;
					
					Logger.info(`TradingView ${chartConfig.market.toUpperCase()} chart initialized:`, chartConfig.symbol);
					// ==========================================
					
					// Optional: Auto-load studies (make this configurable)
					// tvWidget.activeChart().createStudy('RSI', false, false, { length: 14 });
					// tvWidget.activeChart().createStudy('Volume', false, false);
					
					// Subscribe to chart events for analytics
					tvWidget.subscribe('onAutoSaveNeeded', () => {
						Logger.debug('Chart state changed');
					});
				});

			} catch (error) {
				if (!isCancelled) {
					Logger.error('Failed to initialize TradingView chart:', error);
					// Error will be caught by ErrorBoundary
					throw error;
				}
			}
		};

		initChart();

		// Cleanup function - CRITICAL to prevent memory leaks
		return () => {
			isCancelled = true;
			
			if (tvWidgetRef.current) {
				Logger.info('Cleaning up TradingView widget');
				try {
					tvWidgetRef.current.remove();
					tvWidgetRef.current = null;
				} catch (error) {
					Logger.error('Error cleaning up widget:', error);
				}
			}
			
			// Cleanup datafeed
			if (chartConfig.datafeed && typeof chartConfig.datafeed.destroy === 'function') {
				Logger.info('Cleaning up datafeed');
				chartConfig.datafeed.destroy();
			}
		};
	}, [widgetOptions, chartConfig.symbol, chartConfig.market]); // Re-create if config changes

	return (
		<div
			id={containerRef.current}
			className="TVChartContainer"
		/>
	);
};

export default TVChartContainer;
